import React from "react";
import { motion } from "framer-motion";
import { Coffee, DollarSign, Gift, TrendingUp, Sparkles, Calendar } from "lucide-react";

/**
 * MeStatsHero — beautiful glance-card for the customer's total Coffeein7 impact.
 * Renders only when totals?.total_orders > 0.
 */
const MeStatsHero = ({ totals, cafeCount, name }) => {
    if (!totals || (totals.total_orders || 0) === 0) return null;

    const firstName = (name || "").split(" ")[0];
    const memberSinceLabel = totals.member_since
        ? new Date(totals.member_since).toLocaleDateString([], { month: "short", year: "numeric" })
        : null;

    // Pull a fun fact based on lifetime coffees
    const funFact = getFunFact(totals.total_coffees);

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-8 rounded-3xl bg-gradient-to-br from-[#2C1A12] via-[#3B2418] to-[#0E0906] text-white p-6 sm:p-7 relative overflow-hidden"
            data-testid="me-stats-hero"
        >
            {/* Ambient background art */}
            <div className="pointer-events-none absolute -right-10 -top-16 text-[220px] opacity-[0.05] leading-none">☕</div>
            <div className="pointer-events-none absolute -left-6 bottom-0 text-[160px] opacity-[0.04] leading-none">🫘</div>

            <div className="relative">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                    <Sparkles className="h-3 w-3" strokeWidth={2.5} />
                    Your coffee story
                </div>
                <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl leading-tight">
                    {firstName ? `${firstName}, you've had` : "You've had"}{" "}
                    <CountUp value={totals.total_coffees} className="text-[#FF4F18]" />{" "}
                    coffees on us.
                </h2>
                <p className="mt-2 text-sm text-white/60">{funFact}</p>

                {/* Stat grid */}
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <StatTile
                        testid="stat-coffees"
                        icon={Coffee}
                        label="Coffees"
                        value={totals.total_coffees.toLocaleString()}
                        sub={`across ${cafeCount} café${cafeCount === 1 ? "" : "s"}`}
                        accent="#FF4F18"
                    />
                    <StatTile
                        testid="stat-spent"
                        icon={DollarSign}
                        label="Spent"
                        value={`$${totals.total_spent.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                        sub={`${totals.total_orders} order${totals.total_orders === 1 ? "" : "s"}`}
                    />
                    <StatTile
                        testid="stat-savings"
                        icon={Gift}
                        label="Saved"
                        value={`$${totals.total_savings.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                        sub={`${totals.total_free_redeemed} free coffee${totals.total_free_redeemed === 1 ? "" : "s"}`}
                        accent="#1B995E"
                    />
                    <StatTile
                        testid="stat-beans"
                        icon={TrendingUp}
                        label="Beans"
                        value={totals.total_beans_balance.toLocaleString()}
                        sub={`${totals.total_beans_earned.toLocaleString()} earned lifetime`}
                        emojiIcon="🫘"
                    />
                </div>

                {/* Bottom strip: member-since + free-coffee-ready pill */}
                <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
                    {memberSinceLabel && (
                        <div className="flex items-center gap-2 text-[10px] text-white/50 uppercase tracking-[0.25em] font-bold">
                            <Calendar className="h-3 w-3" strokeWidth={2.5} />
                            Member since {memberSinceLabel}
                        </div>
                    )}
                    {totals.free_coffees_ready > 0 && (
                        <div
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#FF4F18] px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold shadow-[0_8px_20px_rgba(255,79,24,0.4)]"
                            data-testid="stat-free-ready"
                        >
                            <Gift className="h-3 w-3" strokeWidth={2.5} />
                            {totals.free_coffees_ready} free coffee{totals.free_coffees_ready === 1 ? "" : "s"} ready
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

const StatTile = ({ icon: Icon, emojiIcon, label, value, sub, accent = "#FFFFFF", testid }) => (
    <div
        data-testid={testid}
        className="rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur px-3 py-3 sm:px-4 sm:py-4"
    >
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] font-bold text-white/50">
            {emojiIcon ? (
                <span className="text-xs leading-none">{emojiIcon}</span>
            ) : (
                <Icon className="h-3 w-3" strokeWidth={2.5} style={{ color: accent }} />
            )}
            {label}
        </div>
        <div
            className="mt-1.5 font-display font-black text-2xl sm:text-3xl leading-none"
            style={accent !== "#FFFFFF" ? { color: accent } : undefined}
        >
            {value}
        </div>
        <div className="mt-1 text-[10px] text-white/40 truncate">{sub}</div>
    </div>
);

/** Lightweight count-up: 900ms tween from 0 → value. */
const CountUp = ({ value, className = "" }) => {
    const [display, setDisplay] = React.useState(0);
    React.useEffect(() => {
        if (!value) {
            setDisplay(0);
            return;
        }
        const start = performance.now();
        const dur = 900;
        let raf;
        const tick = (t) => {
            const p = Math.min(1, (t - start) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(Math.round(value * eased));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => raf && cancelAnimationFrame(raf);
    }, [value]);
    return <span className={className}>{display.toLocaleString()}</span>;
};

/** Fun facts tied to lifetime coffee count. */
function getFunFact(n) {
    if (n <= 0) return "Your first cup is one away.";
    if (n < 5) return `That's ${n * 32}g of coffee brewed just for you.`;
    if (n < 20) return `You've skipped the queue ${n} times. Barista respect: earned.`;
    if (n < 50) return `Stacked end-to-end, that's roughly ${(n * 0.12).toFixed(1)}m of cups.`;
    if (n < 100) return `Enough caffeine to power a marathon — twice.`;
    if (n < 250) return `You've ordered more coffees than 92% of people we've ever seen.`;
    if (n < 500) return `Officially a coffee whisperer. Baristas know your order by ···number.`;
    return `Legendary. That's ${(n * 0.03).toFixed(1)}kg of beans through our roasters.`;
}

export default MeStatsHero;
